import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
} from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, EventModalStyles } from "./styles/adminEvents.js";
import {
  ADMIN_EVENTS_ENDPOINT,
  ADMIN_EVENTS_UPDATE_ENDPOINT,
  ADMIN_GALLERY_ENDPOINT,
  API_BASE_URL,
} from "../../config/api";
import { useAuth } from "../../context/AuthContext";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const initialForm = {
  title: "",
  slug: "",
  description: "",
  location: "",
  startDate: "",
  endDate: "",
  coverImage: "",
  capacity: "",
  status: "draft",
  startTime: "",
};

const AdminEvents = () => {
  const { user, loading: authLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modalState, setModalState] = useState({
    show: false,
    mode: "add",
    event: null,
    form: initialForm,
    saving: false,
    error: "",
  });
  const [deletingId, setDeletingId] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [gallerySelection, setGallerySelection] = useState("");

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  const fetchEvents = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }
      if (statusFilter) {
        params.set("status", statusFilter);
      }

      const response = await fetch(
        `${ADMIN_EVENTS_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load events.");
      }

      const payload = await response.json();
      setEvents(payload.events || []);
      if (payload.pagination) {
        const serverPage = payload.pagination.page || page;
        setPagination({
          page: serverPage,
          limit: payload.pagination.limit || pageSize,
          totalItems: payload.pagination.totalItems || 0,
          totalPages: payload.pagination.totalPages || 1,
        });
        if (serverPage !== page) {
          setPage(serverPage);
        }
      } else {
        setPagination({
          page,
          limit: pageSize,
          totalItems: payload.events?.length || 0,
          totalPages: 1,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to load events.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchEvents();
    }
  }, [authLoading, isAdmin, fetchEvents]);

  const loadGalleryImages = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    setGalleryLoading(true);
    setGalleryError("");
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(ADMIN_GALLERY_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load gallery images.");
      }

      const payload = await response.json();
      setGalleryImages(Array.isArray(payload.images) ? payload.images : []);
    } catch (err) {
      setGalleryError(err.message || "Unable to load gallery images.");
      setGalleryImages([]);
    } finally {
      setGalleryLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (modalState.show) {
      loadGalleryImages();
    }
  }, [modalState.show, loadGalleryImages]);

  useEffect(() => {
    if (!modalState.show) {
      return;
    }
    const current = modalState.form.coverImage;
    if (!current) {
      setGallerySelection("");
      return;
    }

    const isFromGallery = galleryImages.some((image) => {
      const absolute =
        image.url && image.url.startsWith("http")
          ? image.url
          : `${API_BASE_URL}${image.url || ""}`;
      return absolute === current;
    });

    setGallerySelection(isFromGallery ? current : "");
  }, [modalState.show, modalState.form.coverImage, galleryImages]);

  const tableRows = useMemo(
    () =>
      events.map((event) => ({
        id: event._id,
        title: event.title || "Untitled Event",
        slug: event.slug,
        status: event.status
          ? event.status.charAt(0).toUpperCase() + event.status.slice(1)
          : "Draft",
        location: event.location || "TBD",
        startDate: event.startDate
          ? new Date(event.startDate).toLocaleString()
          : "N/A",
        endDate: event.endDate
          ? new Date(event.endDate).toLocaleString()
          : "N/A",
        capacity: typeof event.capacity === "number" ? event.capacity : "N/A",
        raw: event,
      })),
    [events]
  );

  const openModal = (mode, event = null) => {
    if (mode === "edit" && event) {
      setModalState({
        show: true,
        mode,
        event,
        form: {
          title: event.title || "",
          slug: event.slug || "",
          description: event.description || "",
          location: event.location || "",
          startDate: event.startDate
            ? new Date(event.startDate).toISOString().slice(0, 16)
            : "",
          endDate: event.endDate
            ? new Date(event.endDate).toISOString().slice(0, 16)
            : "",
          coverImage: event.coverImage || "",
          capacity:
            typeof event.capacity === "number" ? String(event.capacity) : "",
          status: event.status || "draft",
        },
        saving: false,
        error: "",
      });
      setGallerySelection(event.coverImage || "");
    } else {
      setModalState({
        show: true,
        mode: "add",
        event: null,
        form: initialForm,
        saving: false,
        error: "",
      });
      setGallerySelection("");
    }
  };

  const closeModal = () => {
    setModalState({
      show: false,
      mode: "add",
      event: null,
      form: initialForm,
      saving: false,
      error: "",
    });
    setGallerySelection("");
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
      error: "",
    }));
    if (name === "coverImage") {
      setGallerySelection("");
    }
  };

  const handleGallerySelect = (event) => {
    const selected = event.target.value;
    setGallerySelection(selected);
    if (!selected) {
      return;
    }
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        coverImage: selected,
      },
      error: "",
    }));
  };

  const submitModal = async (event) => {
    event.preventDefault();
    setModalState((prev) => ({ ...prev, saving: true, error: "" }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      if (!modalState.form.title.trim()) {
        throw new Error("Title is required.");
      }

      const payload = {
        title: modalState.form.title.trim(),
        slug: modalState.form.slug.trim(),
        description: modalState.form.description.trim(),
        location: modalState.form.location.trim(),
        startDate: modalState.form.startDate || undefined,
        endDate: modalState.form.endDate || undefined,
        coverImage: modalState.form.coverImage.trim(),
        status: modalState.form.status,
        capacity: modalState.form.capacity
          ? Number(modalState.form.capacity)
          : undefined,
      };

      const requestOptions = {
        method: modalState.mode === "add" ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      };

      const endpoint =
        modalState.mode === "add"
          ? ADMIN_EVENTS_ENDPOINT
          : ADMIN_EVENTS_UPDATE_ENDPOINT(modalState.event._id);

      const response = await fetch(endpoint, requestOptions);

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Unable to save event.");
      }

      await response.json();
      closeModal();
      fetchEvents();
    } catch (err) {
      setModalState((prev) => ({
        ...prev,
        error: err.message || "Unable to save event.",
      }));
    } finally {
      setModalState((prev) => ({ ...prev, saving: false }));
    }
  };

  const deleteEvent = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this event?"
    );
    if (!confirmDelete) {
      return;
    }
    setDeletingId(id);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(`${ADMIN_EVENTS_UPDATE_ENDPOINT(id)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to delete event.");
      }

      fetchEvents();
    } catch (err) {
      setError(err.message || "Unable to delete event.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <Styles>
      <EventModalStyles />
      <div className="main-wrapper admin-events-page">
        <HeaderTwo />
        <BreadcrumbBox title="Manage Events" />

        <section className="events-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}
                {isAdmin && (
                  <div className="card-box">
                    <div className="actions">
                      <Button onClick={() => openModal("add")}>
                        Add Event
                      </Button>
                      <div className="filters">
                        <input
                          type="text"
                          placeholder="Search events..."
                          value={searchInput}
                          onChange={(event) =>
                            setSearchInput(event.target.value)
                          }
                          disabled={loading}
                        />
                        <select
                          value={statusFilter}
                          onChange={(event) => {
                            setStatusFilter(event.target.value);
                            setPage(1);
                          }}
                          disabled={loading}
                        >
                          <option value="">All Statuses</option>
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            setSearchInput("");
                            setSearchTerm("");
                            setStatusFilter("");
                            setPage(1);
                          }}
                          disabled={loading}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="success"
                          onClick={() => {
                            setSearchTerm(searchInput.trim());
                            setPage(1);
                          }}
                          disabled={loading}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                    {error && (
                      <Alert variant="danger" className="mt-3">
                        {error}
                      </Alert>
                    )}
                    {loading ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="data-grid">
                        {tableRows.length === 0 ? (
                          <Alert variant="info" className="mb-0">
                            No events found.
                          </Alert>
                        ) : (
                          <>
                            <table>
                              <thead>
                                <tr>
                                  <th>Title</th>
                                  <th>Status</th>
                                  <th>Location</th>
                                  <th>Dates</th>
                                  <th>Capacity</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableRows.map((row) => (
                                  <tr key={row.id}>
                                    <td>
                                      <div className="blog-title-cell">
                                        <strong>{row.title}</strong>
                                        <span
                                          className="slug"
                                          style={{ marginLeft: "10px" }}
                                        >
                                          {row.slug}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="text-capitalize">
                                      {row.status}
                                    </td>
                                    <td>{row.location}</td>
                                    <td className="date-cell">
                                      <span>Start: {row.startDate}</span>
                                      <span>End: {row.endDate}</span>
                                    </td>
                                    <td>{row.capacity}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="link-button"
                                        onClick={() =>
                                          openModal("edit", row.raw)
                                        }
                                      >
                                        Edit
                                      </button>
                                      <span> | </span>
                                      <button
                                        type="button"
                                        className="link-button"
                                        onClick={() => deleteEvent(row.id)}
                                        disabled={deletingId === row.id}
                                      >
                                        {deletingId === row.id
                                          ? "Deleting..."
                                          : "Delete"}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="pagination-controls">
                              <div className="page-info">
                                Page {pagination.page} of{" "}
                                {pagination.totalPages} ·{" "}
                                {pagination.totalItems} total
                              </div>
                              <div className="page-actions">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPage((prev) => Math.max(prev - 1, 1))
                                  }
                                  disabled={pagination.page <= 1 || loading}
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPage((prev) =>
                                      Math.min(prev + 1, pagination.totalPages)
                                    )
                                  }
                                  disabled={
                                    pagination.page >= pagination.totalPages ||
                                    loading
                                  }
                                >
                                  Next
                                </button>
                                <select
                                  value={pageSize}
                                  onChange={(event) => {
                                    const newSize =
                                      Number(event.target.value) || 10;
                                    setPageSize(newSize);
                                    setPage(1);
                                  }}
                                  disabled={loading}
                                >
                                  {[10, 20, 50, 100].map((size) => (
                                    <option key={size} value={size}>
                                      {size} / page
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={modalState.show}
        onHide={closeModal}
        centered
        dialogClassName="admin-events-modal"
        size="lg"
      >
        <Form onSubmit={submitModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalState.mode === "add" ? "Add Event" : "Edit Event"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalState.error && (
              <Alert variant="danger">{modalState.error}</Alert>
            )}
            <Row>
              <Col md="6">
                <Form.Group controlId="event_title">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={modalState.form.title}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="event_slug">
                  <Form.Label>Slug</Form.Label>
                  <Form.Control
                    type="text"
                    name="slug"
                    value={modalState.form.slug}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder="leave blank to auto-generate"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="6">
                <Form.Group controlId="event_location">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={modalState.form.location}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder="City, Venue"
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group controlId="event_start">
                  <Form.Label>Start Date & Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="startDate"
                    value={modalState.form.startDate}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  />
                </Form.Group>
              </Col>{" "}
              <Col md="3">
                <Form.Group controlId="event_end">
                  <Form.Label>End Date & Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="endDate"
                    value={modalState.form.endDate}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="6">
                <Form.Group controlId="event_cover">
                  <Form.Label>Cover Image URL</Form.Label>
                  <Form.Control
                    type="text"
                    name="coverImage"
                    value={modalState.form.coverImage}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder="https://example.com/image.jpg"
                  />
                  <div className="gallery-picker">
                    <span className="muted">
                      Paste an external URL or choose an uploaded gallery image.
                    </span>
                    {galleryLoading ? (
                      <div className="gallery-status">
                        <Spinner animation="border" size="sm" role="status" />
                        <span>Loading gallery…</span>
                      </div>
                    ) : (
                      <>
                        <Form.Control
                          as="select"
                          value={gallerySelection}
                          onChange={handleGallerySelect}
                          disabled={
                            modalState.saving || galleryImages.length === 0
                          }
                        >
                          <option value="">Select from gallery…</option>
                          {galleryImages.map((image) => {
                            const absolute =
                              image.url && image.url.startsWith("http")
                                ? image.url
                                : `${API_BASE_URL}${image.url || ""}`;
                            return (
                              <option key={image.fileName} value={absolute}>
                                {image.fileName}
                              </option>
                            );
                          })}
                        </Form.Control>
                        {galleryImages.length === 0 && !galleryError && (
                          <span className="muted">
                            No gallery images available yet.
                          </span>
                        )}
                      </>
                    )}
                    {galleryError && (
                      <span className="text-danger small">{galleryError}</span>
                    )}
                  </div>
                  {modalState.form.coverImage && (
                    <div className="cover-preview">
                      <span className="preview-label">Preview</span>
                      <img
                        src={modalState.form.coverImage}
                        alt="Event cover preview"
                        className="img-fluid"
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group controlId="event_capacity">
                  <Form.Label>Capacity</Form.Label>
                  <Form.Control
                    type="number"
                    name="capacity"
                    value={modalState.form.capacity}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group controlId="event_status">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    as="select"
                    name="status"
                    value={modalState.form.status}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="12">
                <Form.Group controlId="event_description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="description"
                    value={modalState.form.description}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder="Event details"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={modalState.saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={modalState.saving}
            >
              {modalState.saving ? "Saving..." : "Save Event"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Styles>
  );
};

export default AdminEvents;
